/**
 * Convert a small markdown subset to HTML for Materiah AI assistant bubbles.
 * Supports: **bold**, __bold__, *italic*, _italic_, `code`, - / * lists,
 * numbered lists, [links](https://...) and in-app [links](/path), and newlines.
 * Output must be passed through DOMPurify before rendering.
 *
 * @param {string|null|undefined} text
 * @returns {string} HTML string (unsanitized)
 */
export function markdownSubsetToHtml(text) {
  if (text == null || text === '') {
    return '';
  }

  const source = String(text);
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let listType = null; // 'ul' | 'ol' | null
  let listItems = [];

  const flushList = () => {
    if (!listType || listItems.length === 0) {
      listType = null;
      listItems = [];
      return;
    }
    const tag = listType;
    blocks.push(
      `<${tag}>${listItems.map(item => `<li>${item}</li>`).join('')}</${tag}>`
    );
    listType = null;
    listItems = [];
  };

  for (const rawLine of lines) {
    const ulMatch = rawLine.match(/^\s*[-*]\s+(.+)$/);
    const olMatch = rawLine.match(/^\s*\d+[.)]\s+(.+)$/);

    if (ulMatch) {
      if (listType && listType !== 'ul') flushList();
      listType = 'ul';
      listItems.push(inlineMarkdownToHtml(ulMatch[1]));
      continue;
    }
    if (olMatch) {
      if (listType && listType !== 'ol') flushList();
      listType = 'ol';
      listItems.push(inlineMarkdownToHtml(olMatch[1]));
      continue;
    }

    flushList();

    if (rawLine.trim() === '') {
      blocks.push('<br />');
      continue;
    }

    blocks.push(`<p>${inlineMarkdownToHtml(rawLine)}</p>`);
  }

  flushList();
  return blocks.join('');
}

/**
 * Escape HTML, then apply inline markdown transforms.
 *
 * @param {string} text
 * @returns {string}
 */
export function inlineMarkdownToHtml(text) {
  let s = escapeHtml(text);

  // Code spans first so we do not transform markdown inside them
  const codeSlots = [];
  s = s.replace(/`([^`]+)`/g, (_, code) => {
    const token = `\u0000CODE${codeSlots.length}\u0000`;
    codeSlots.push(`<code>${code}</code>`);
    return token;
  });

  // Links: https://... or safe same-origin app paths (/orders, /projects/12, …)
  s = s.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+|\/[a-zA-Z0-9][a-zA-Z0-9/_?&=.%~-]*)\)/gi,
    (_, label, href) => {
      const isAppPath = href.startsWith('/');
      if (isAppPath) {
        return `<a href="${href}" class="ai-chat-app-link">${label}</a>`;
      }
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`;
    }
  );

  // Bold before italic
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  s = s.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  s = s.replace(/_([^_]+)_/g, '<em>$1</em>');

  codeSlots.forEach((html, i) => {
    s = s.replace(`\u0000CODE${i}\u0000`, html);
  });

  return s;
}

/**
 * @param {string} text
 * @returns {string}
 */
export function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
